import { useState, type ReactNode } from 'react';
import { ArrowCounterClockwise, FloppyDisk, Pencil, Plus, Trash } from '@phosphor-icons/react';
import type { InterviewPackage, ProjectItem, StarQA, StudyGuideItem } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, EmptyState, ErrorState, LoadingState, Modal, Pill, Tabs } from '../components/ui';

const uid = () => Math.random().toString(36).slice(2, 10);
const csv = (s: string) => s.split(',').map((t) => t.trim()).filter(Boolean);
const lines = (s: string) => s.split('\n').map((t) => t.trim()).filter(Boolean);

export function StudioInterview() {
  const state = useAsync(() => api.interviewPackages(), []);
  const [busy, setBusy] = useState(false);

  if (state.loading) return <LoadingState />;
  if (state.error || !state.data) return <ErrorState message={state.error ?? 'Failed to load'} onRetry={state.reload} />;

  const pkg = state.data[0] ?? null;

  if (!pkg) {
    return (
      <div>
        <Heading />
        <EmptyState
          title="No interview package yet"
          description="Create one to build the study guide, STAR Q&A and project portfolio."
          action={
            <Button
              icon={Plus}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.admin.createInterview();
                  state.reload();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Create package
            </Button>
          }
        />
      </div>
    );
  }

  return <Editor key={pkg.id} server={pkg} onSaved={state.reload} />;
}

function Heading() {
  return (
    <div className="mb-5">
      <div className="label-mono">Interview Prep</div>
      <h2 className="text-xl font-bold">Interview package</h2>
      <p className="mt-1 text-sm text-slate-500">Study guide, STAR Q&amp;A bank, and project portfolio shown to learners.</p>
    </div>
  );
}

type ModalState =
  | { section: 'studyGuide'; index: number | null }
  | { section: 'starQA'; index: number | null }
  | { section: 'projects'; index: number | null }
  | null;

function Editor({ server, onSaved }: { server: InterviewPackage; onSaved: () => void }) {
  const [pkg, setPkg] = useState<InterviewPackage>(() => structuredClone(server));
  const [tab, setTab] = useState('studyGuide');
  const [modal, setModal] = useState<ModalState>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(pkg) !== JSON.stringify(server);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.admin.updateInterview(pkg.id, {
        role: pkg.role,
        company: pkg.company,
        studyGuide: pkg.studyGuide,
        starQA: pkg.starQA,
        projects: pkg.projects,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function upsert(section: 'studyGuide' | 'starQA' | 'projects', item: { id: string }, index: number | null) {
    setPkg((p) => {
      const list = [...(p[section] as { id: string }[])];
      if (index == null) list.push(item);
      else list[index] = item;
      return { ...p, [section]: list } as InterviewPackage;
    });
  }
  function removeAt(section: 'studyGuide' | 'starQA' | 'projects', index: number) {
    setPkg(
      (p) => ({ ...p, [section]: (p[section] as { id: string }[]).filter((_, i) => i !== index) }) as InterviewPackage,
    );
  }

  return (
    <div>
      <Heading />

      <Card className="mb-4 p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label-mono mb-1 block">Role</span>
            <input className="input" value={pkg.role} onChange={(e) => setPkg((p) => ({ ...p, role: e.target.value }))} />
          </label>
          <label className="block">
            <span className="label-mono mb-1 block">Target company</span>
            <input className="input" value={pkg.company} onChange={(e) => setPkg((p) => ({ ...p, company: e.target.value }))} />
          </label>
        </div>
      </Card>

      <Tabs
        className="mb-4"
        active={tab}
        onChange={setTab}
        items={[
          { id: 'studyGuide', label: `Study Guide (${pkg.studyGuide.length})` },
          { id: 'starQA', label: `STAR Q&A (${pkg.starQA.length})` },
          { id: 'projects', label: `Projects (${pkg.projects.length})` },
        ]}
      />

      {tab === 'studyGuide' && (
        <Section
          items={pkg.studyGuide}
          onAdd={() => setModal({ section: 'studyGuide', index: null })}
          onEdit={(i) => setModal({ section: 'studyGuide', index: i })}
          onRemove={(i) => removeAt('studyGuide', i)}
          render={(it) => (
            <>
              <div className="font-semibold text-brand-navy">{it.concept}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {it.tools.map((t) => (
                  <Pill key={t} tone="teal">
                    {t}
                  </Pill>
                ))}
              </div>
              <p className="mt-1 text-sm text-slate-500">{it.whyItMatters}</p>
            </>
          )}
        />
      )}
      {tab === 'starQA' && (
        <Section
          items={pkg.starQA}
          onAdd={() => setModal({ section: 'starQA', index: null })}
          onEdit={(i) => setModal({ section: 'starQA', index: i })}
          onRemove={(i) => removeAt('starQA', i)}
          render={(it) => (
            <>
              <div className="label-mono">{it.competency}</div>
              <div className="font-semibold text-brand-navy">{it.question}</div>
            </>
          )}
        />
      )}
      {tab === 'projects' && (
        <Section
          items={pkg.projects}
          onAdd={() => setModal({ section: 'projects', index: null })}
          onEdit={(i) => setModal({ section: 'projects', index: i })}
          onRemove={(i) => removeAt('projects', i)}
          render={(it) => (
            <>
              <div className="font-semibold text-brand-navy">{it.title}</div>
              <p className="mt-1 text-sm text-slate-500">{it.summary}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {it.skills.map((s) => (
                  <Pill key={s} tone="slate">
                    {s}
                  </Pill>
                ))}
              </div>
            </>
          )}
        />
      )}

      {/* Save bar */}
      <div className="sticky bottom-0 mt-5 flex items-center justify-end gap-3 border-t border-slate-200 bg-brand-light/80 py-3 backdrop-blur">
        {error && <span className="mr-auto text-sm font-medium text-rose-600">{error}</span>}
        {dirty && <span className="mr-auto text-sm text-amber-600">Unsaved changes</span>}
        <Button variant="secondary" icon={ArrowCounterClockwise} disabled={!dirty || busy} onClick={() => setPkg(structuredClone(server))}>
          Discard
        </Button>
        <Button icon={FloppyDisk} loading={busy} disabled={!dirty} onClick={save}>
          Save changes
        </Button>
      </div>

      {modal?.section === 'studyGuide' && (
        <Modal open onClose={() => setModal(null)} title={modal.index == null ? 'Add concept' : 'Edit concept'}>
          <StudyGuideForm
            initial={modal.index == null ? undefined : pkg.studyGuide[modal.index]}
            onCancel={() => setModal(null)}
            onSubmit={(it) => {
              upsert('studyGuide', it, modal.index);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.section === 'starQA' && (
        <Modal open onClose={() => setModal(null)} title={modal.index == null ? 'Add STAR question' : 'Edit STAR question'}>
          <StarForm
            initial={modal.index == null ? undefined : pkg.starQA[modal.index]}
            onCancel={() => setModal(null)}
            onSubmit={(it) => {
              upsert('starQA', it, modal.index);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.section === 'projects' && (
        <Modal open onClose={() => setModal(null)} title={modal.index == null ? 'Add project' : 'Edit project'}>
          <ProjectForm
            initial={modal.index == null ? undefined : pkg.projects[modal.index]}
            onCancel={() => setModal(null)}
            onSubmit={(it) => {
              upsert('projects', it, modal.index);
              setModal(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function Section<T extends { id: string }>({
  items,
  render,
  onAdd,
  onEdit,
  onRemove,
}: {
  items: T[];
  render: (item: T) => ReactNode;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <Card key={it.id} className="flex items-start justify-between gap-3 p-4">
          <div className="min-w-0">{render(it)}</div>
          <div className="flex shrink-0 gap-1">
            <button className="btn-ghost p-1.5" aria-label="Edit" onClick={() => onEdit(i)}>
              <Pencil size={16} />
            </button>
            <button
              className="btn-ghost p-1.5 text-rose-600"
              aria-label="Remove"
              onClick={() => {
                if (confirm('Remove this item?')) onRemove(i);
              }}
            >
              <Trash size={16} />
            </button>
          </div>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-400">Nothing here yet.</p>}
      <Button variant="secondary" icon={Plus} onClick={onAdd}>
        Add item
      </Button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label-mono mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function FormButtons({ busy, onCancel }: { busy?: boolean; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <Button type="button" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" loading={busy}>
        Done
      </Button>
    </div>
  );
}

function StudyGuideForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: StudyGuideItem;
  onSubmit: (item: StudyGuideItem) => void;
  onCancel: () => void;
}) {
  const [concept, setConcept] = useState(initial?.concept ?? '');
  const [tools, setTools] = useState(initial?.tools.join(', ') ?? '');
  const [why, setWhy] = useState(initial?.whyItMatters ?? '');
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ id: initial?.id ?? uid(), concept: concept.trim(), tools: csv(tools), whyItMatters: why.trim() });
      }}
    >
      <Field label="Concept">
        <input className="input" value={concept} onChange={(e) => setConcept(e.target.value)} required />
      </Field>
      <Field label="Tools (comma-separated)">
        <input className="input" value={tools} onChange={(e) => setTools(e.target.value)} placeholder="Sentinel, KQL" />
      </Field>
      <Field label="Why it matters">
        <textarea className="input" rows={3} value={why} onChange={(e) => setWhy(e.target.value)} />
      </Field>
      <FormButtons onCancel={onCancel} />
    </form>
  );
}

function StarForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: StarQA;
  onSubmit: (item: StarQA) => void;
  onCancel: () => void;
}) {
  const [competency, setCompetency] = useState(initial?.competency ?? '');
  const [question, setQuestion] = useState(initial?.question ?? '');
  const [situation, setSituation] = useState(initial?.star.situation ?? '');
  const [task, setTask] = useState(initial?.star.task ?? '');
  const [action, setAction] = useState(initial?.star.action ?? '');
  const [result, setResult] = useState(initial?.star.result ?? '');
  const [modelAnswer, setModelAnswer] = useState(initial?.modelAnswer ?? '');
  return (
    <form
      className="max-h-[70vh] space-y-4 overflow-y-auto pr-1"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          id: initial?.id ?? uid(),
          competency: competency.trim(),
          question: question.trim(),
          star: { situation: situation.trim(), task: task.trim(), action: action.trim(), result: result.trim() },
          modelAnswer: modelAnswer.trim(),
        });
      }}
    >
      <Field label="Competency">
        <input className="input" value={competency} onChange={(e) => setCompetency(e.target.value)} placeholder="Incident response" />
      </Field>
      <Field label="Question">
        <input className="input" value={question} onChange={(e) => setQuestion(e.target.value)} required />
      </Field>
      <Field label="Situation">
        <textarea className="input" rows={2} value={situation} onChange={(e) => setSituation(e.target.value)} />
      </Field>
      <Field label="Task">
        <textarea className="input" rows={2} value={task} onChange={(e) => setTask(e.target.value)} />
      </Field>
      <Field label="Action">
        <textarea className="input" rows={2} value={action} onChange={(e) => setAction(e.target.value)} />
      </Field>
      <Field label="Result">
        <textarea className="input" rows={2} value={result} onChange={(e) => setResult(e.target.value)} />
      </Field>
      <Field label="Model answer">
        <textarea className="input" rows={3} value={modelAnswer} onChange={(e) => setModelAnswer(e.target.value)} />
      </Field>
      <FormButtons onCancel={onCancel} />
    </form>
  );
}

function ProjectForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: ProjectItem;
  onSubmit: (item: ProjectItem) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [summary, setSummary] = useState(initial?.summary ?? '');
  const [skills, setSkills] = useState(initial?.skills.join(', ') ?? '');
  const [repoUrl, setRepoUrl] = useState(initial?.repoUrl ?? '');
  const [highlights, setHighlights] = useState(initial?.highlights.join('\n') ?? '');
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          id: initial?.id ?? uid(),
          title: title.trim(),
          summary: summary.trim(),
          skills: csv(skills),
          repoUrl: repoUrl.trim() || null,
          highlights: lines(highlights),
        });
      }}
    >
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </Field>
      <Field label="Summary">
        <textarea className="input" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </Field>
      <Field label="Skills (comma-separated)">
        <input className="input" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Sentinel, KQL, Bicep" />
      </Field>
      <Field label="Repo URL (optional)">
        <input className="input" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} placeholder="https://github.com/…" />
      </Field>
      <Field label="Highlights (one per line)">
        <textarea className="input" rows={3} value={highlights} onChange={(e) => setHighlights(e.target.value)} />
      </Field>
      <FormButtons onCancel={onCancel} />
    </form>
  );
}
